// ============================================================
// TMR Portal Admin — holding a modal's unsaved work against every way out of it
// ============================================================
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * The mark on the history entry a guarded modal pushes when it opens.
 *
 * The entry is a copy of the page's own — same URL — so the address bar does not move and a refresh lands on
 * the same screen. What tells the two apart is this key in the router's location state.
 */
const GUARD_KEY = 'unsavedChangesGuard';

/** Whether a router location state is the guard's own entry. */
function isGuardEntry(state: unknown): boolean {
  return typeof state === 'object' && state !== null && (state as Record<string, unknown>)[GUARD_KEY] === true;
}

/** Where the guard stands in its handshake with the browser history. */
export interface HistoryGuardState {
  /** The current entry is the one the guard pushed. */
  onGuardEntry: boolean;
  /** The guard has asked for its entry, and may not have seen it land yet. */
  pushed: boolean;
  /** The guard has seen its entry land — leaving it from here on is the operator going back. */
  armed: boolean;
  /** The modal is closing on purpose, so a history move from here on is the guard's own cleanup. */
  leaving: boolean;
  /** There is unsaved work. */
  dirty: boolean;
}

/**
 * What the guard does when the history entry under the modal changes.
 *
 * - `push` — the modal just opened: add the guard's entry on top of the page's.
 * - `wait` — the entry was asked for and has not landed yet. React's development double run of effects comes
 *   through here, which is why the request is remembered rather than repeated.
 * - `arm` — the guard's entry is current; from now on, leaving it means the operator went back.
 * - `ignore` — the modal is on its way out, and the move is its own.
 * - `close` — the operator went back with nothing to lose: Back closes the modal, and the page stays.
 * - `confirm` — the operator went back with unsaved work: ask, and put the entry back in the meantime.
 */
export type HistoryReaction = 'push' | 'wait' | 'arm' | 'ignore' | 'close' | 'confirm';

/**
 * Decides the guard's {@link HistoryReaction} — the whole handshake, apart from the router, so it can be
 * tested without a browser.
 *
 * @param state Where the guard stands when the entry changed.
 */
export function reactToHistory(state: HistoryGuardState): HistoryReaction {
  if (state.leaving) return 'ignore';
  if (state.onGuardEntry) return 'arm';
  if (!state.armed) return state.pushed ? 'wait' : 'push';
  return state.dirty ? 'confirm' : 'close';
}

/** Options for {@link useUnsavedChangesGuard}. */
export interface UnsavedChangesGuardOptions {
  /** Whether closing now would lose work. */
  dirty: boolean;
  /** Closes the modal. */
  onClose: () => void;
}

/** What a guarded modal wires to its own exits and to its confirmation dialog. */
export interface UnsavedChangesGuard {
  /** For the modal's own exits — the close button, a click on the backdrop. Asks first when there is work to lose. */
  requestClose: () => void;
  /** Whether the "descartar?" confirmation should be on screen. */
  confirming: boolean;
  /** The operator confirmed: close, and let the work go. */
  discard: () => void;
  /** The operator changed their mind: dismiss the confirmation and stay. */
  keepEditing: () => void;
}

/**
 * Holds a modal's unsaved work against every way out of it, not only its own close button.
 *
 * - **Its own exits** (the close button, the backdrop) go through `requestClose`.
 * - **Back** — the browser's button, Alt+←, a mouse's side button, a trackpad swipe. The portal runs on a
 *   `BrowserRouter`, which has no navigation blocker (`useBlocker` needs a data router), so the guard pushes an
 *   entry of its own when the modal opens: Back then lands on the page's entry, same URL, with the modal still
 *   mounted, and the guard can ask. It puts its entry back while asking, so a second Back is caught too. With
 *   nothing to lose, Back simply closes the modal and the operator stays on the page.
 * - **Leaving the page** — refresh, closing the tab, another address: the browser's own `beforeunload`
 *   prompt, the only thing that can stop those, armed only while there is work to lose.
 *
 * In-app links are not on the list because the modal's backdrop covers the whole portal, sidebar included.
 *
 * The modal's entry is still the current one when it hands control back after a success, so whoever navigates
 * next should `replace` it — otherwise Back from the new page lands on a copy of the one the modal opened on.
 */
export function useUnsavedChangesGuard({ dirty, onClose }: UnsavedChangesGuardOptions): UnsavedChangesGuard {
  const location = useLocation();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const pushed = useRef(false);
  const armed = useRef(false);
  const leaving = useRef(false);
  // The history effect runs once per entry, so it reads these through a ref: it has to see the values of the
  // render it runs after, not those of the render that first scheduled it.
  const latest = useRef({ dirty, onClose });
  latest.current = { dirty, onClose };
  const onGuardEntry = isGuardEntry(location.state);

  useEffect(() => {
    const pushGuardEntry = () =>
      navigate(
        { pathname: location.pathname, search: location.search, hash: location.hash },
        { state: { [GUARD_KEY]: true } },
      );

    const reaction = reactToHistory({
      onGuardEntry,
      pushed: pushed.current,
      armed: armed.current,
      leaving: leaving.current,
      dirty: latest.current.dirty,
    });

    switch (reaction) {
      case 'push':
        pushed.current = true;
        pushGuardEntry();
        break;
      case 'arm':
        armed.current = true;
        break;
      case 'confirm':
        setConfirming(true);
        pushGuardEntry();
        break;
      case 'close':
        leaving.current = true;
        latest.current.onClose();
        break;
    }
    // Keyed on the entry rather than on every render: the guard reacts to history moving, nothing else.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Required by Chrome for the prompt to appear; the wording is the browser's, not ours.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const leave = () => {
    leaving.current = true;
    setConfirming(false);
    onClose();
    // Takes the guard's entry along, so the next Back leaves the page instead of landing on a copy of it.
    if (onGuardEntry) navigate(-1);
  };

  return {
    requestClose: () => (dirty ? setConfirming(true) : leave()),
    confirming,
    discard: leave,
    keepEditing: () => setConfirming(false),
  };
}
